import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'

interface UseErrorRecoveryOptions {
	maxRetries?: number
	retryDelay?: number
	onRetrySuccess?: () => void
	onRetryFailure?: (error: Error) => void
}

export function useErrorRecovery(options: UseErrorRecoveryOptions = {}) {
	const { 
		maxRetries = 3, 
		retryDelay = 1000,
		onRetrySuccess,
		onRetryFailure 
	} = options
	
	const [retryCount, setRetryCount] = useState(0)
	const [isRetrying, setIsRetrying] = useState(false)
	const queryClient = useQueryClient()
	const { toast } = useToast()

	const retry = useCallback(async (
		queryKeys: string[][] | string[],
		customMessage?: string
	) => {
		if (retryCount >= maxRetries) {
			const error = new Error('Maximum retry attempts reached')
			onRetryFailure?.(error)
			toast({
				title: 'Unable to load data',
				description: 'Please refresh the page to try again.',
				variant: 'destructive',
			})
			return false
		}

		setIsRetrying(true)
		
		try {
			// Ensure queryKeys is always an array of arrays
			const keysToInvalidate = Array.isArray(queryKeys[0]) 
				? queryKeys as string[][]
				: [queryKeys as string[]]
			
			// Invalidate all specified queries
			await Promise.all(
				keysToInvalidate.map(key => 
					queryClient.invalidateQueries({ queryKey: key })
				)
			)
			
			// Wait for queries to settle
			await new Promise(resolve => setTimeout(resolve, retryDelay))
			
			// Check if queries have data now
			const allQueriesHaveData = keysToInvalidate.every(key => {
				const query = queryClient.getQueryState(key)
				return query?.data !== undefined && query?.error === null
			})
			
			if (allQueriesHaveData) {
				setRetryCount(0)
				onRetrySuccess?.()
				toast({
					title: 'Data loaded successfully',
					description: customMessage || 'Your data has been loaded.',
				})
				return true
			} else {
				throw new Error('Some queries still have no data')
			}
		} catch (error) {
			setRetryCount(prev => prev + 1)
			console.error('Retry failed:', error)
			
			if (retryCount + 1 < maxRetries) {
				toast({
					title: 'Retrying...',
					description: `Attempt ${retryCount + 2} of ${maxRetries}`,
				})
				// Recursive retry with exponential backoff
				setTimeout(() => {
					retry(queryKeys, customMessage)
				}, retryDelay * Math.pow(2, retryCount + 1))
			} else {
				onRetryFailure?.(error as Error)
			}
			return false
		} finally {
			setIsRetrying(false)
		}
	}, [retryCount, maxRetries, retryDelay, queryClient, toast, onRetrySuccess, onRetryFailure])

	const reset = useCallback(() => {
		setRetryCount(0)
		setIsRetrying(false)
	}, [])

	return {
		retry,
		reset,
		isRetrying,
		retryCount,
		canRetry: retryCount < maxRetries
	}
} 